<script setup lang="ts">
import { ref } from 'vue';
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';
import type { IconName } from '@/helpers/icons';
import type { LendingProduct } from '@/types';

/**
 * Customer ERHL copy of `views/ProductSelectionStep.vue`. The customer journey only offers ERHL,
 * so the Bridging Loan option is dropped and ERHL starts selected, keeping the same look as the
 * broker product selection.
 */
const productOptions: Array<{ id: LendingProduct; title: string; icon: IconName; description: string }> = [
  {
    id: 'erhl',
    title: 'Equity Release Home Loan',
    icon: 'timeLoan',
    description:
      'Unlock the equity in your home to support your lifestyle, financial goals or retirement - delaying the need to sell.',
  },
];

const selectedProduct = ref<LendingProduct>('erhl');

const emit = defineEmits<{ select: [product: LendingProduct] }>();

/**
 * Emits the chosen product to the parent.
 * @returns {void}
 */
const handleNext = (): void => {
  emit('select', selectedProduct.value);
};
</script>

<template>
  <div class="flex flex-1 flex-col">
    <div class="mt-8">
      <RadioGroup v-model="selectedProduct">
        <div class="grid grid-cols-1 gap-4">
          <RadioGroupOption
            v-for="product in productOptions"
            v-slot="{ checked, active }"
            :key="product.id"
            :value="product.id"
            class="hover:border-bridgit-vibrantGreen"
            as="template"
          >
            <div>
              <div
                :class="[
                  checked ? 'border-transparent' : 'border-gray-300',
                  active
                    ? 'border-bridgit-vibrantGreen ring-2 ring-bridgit-vibrantGreen'
                    : 'hover:border-bridgit-lightGreen hover:ring-2 hover:ring-bridgit-lightGreen',
                  'relative flex h-full cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none ',
                ]"
              >
                <span class="flex gap-4">
                  <div class="basis-12 pt-2 text-bridgit-vibrantGreen">
                    <BxIcon :name="product.icon" />
                  </div>
                  <span class="flex flex-grow flex-col">
                    <RadioGroupLabel as="span" class="mb-2 block text-justify text-sm font-medium text-gray-900">
                      {{ product.title }}
                    </RadioGroupLabel>
                    <RadioGroupDescription as="span" class="mt-1 flex items-center text-left text-sm text-gray-500">
                      {{ product.description }}
                    </RadioGroupDescription>
                  </span>
                </span>
                <span
                  :class="[
                    active ? 'border' : 'border-2',
                    checked ? 'border-bridgit-vibrantGreen' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg',
                  ]"
                  aria-hidden="true"
                />
              </div>
            </div>
          </RadioGroupOption>
        </div>
      </RadioGroup>

      <div class="mt-8 rounded-lg bg-bridgit-paleBlue p-6 leading-7">
        <p class="text-left font-bold">Below is what you'll need to complete the application:</p>
        <ul class="mt-4 list-none pl-2 text-left">
          <li><span>Details of the property or properties being used as security</span></li>
          <li><span>Details of all applicants on the application</span></li>
          <li><span>Details of income, expense, asset and liabilities</span></li>
          <li><span>Loan structure (lump sum, with an optional scheduled equity release)</span></li>
        </ul>
      </div>
    </div>

    <div class="mt-auto flex justify-end pt-6">
      <BxButton id="product-selection-next" data-test-id="product-selection-next" variant="primary" @click="handleNext">
        Next
      </BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
ul {
  li {
    @apply mt-0.5;

    &::before {
      @apply text-bridgit-vibrantGreen;
      @apply mr-[-1.125rem] rounded-full font-black;
      content: '✓';
      padding: 2px 4px;
      font-size: 10px;
      background-color: #eeecec;
    }
  }

  span {
    @apply relative left-6 top-[2px] mr-4 inline-flex;
  }
}
</style>
